import type { Product } from "../product.entity";

export type CatalogMatchResult = {
  /** Só quando o código bate com exatamente um produto. */
  autoByCode: Product | null;
  /** Por nome (trecho / igualdade), para sugestão manual. */
  candidatesByName: Product[];
};

/**
 * Por código: match exato em `code` ou id legado.
 * Por nome: igualdade, um contém o outro (trechos).
 */
export function matchCatalogForBulkRow(
  row: { code: string; name: string },
  catalog: Product[]
): CatalogMatchResult {
  const code = row.code?.trim();
  const name = row.name?.trim().toLowerCase();
  let autoByCode: Product | null = null;
  const candidatesByName: Product[] = [];
  const seen = new Set<string>();
  const pushName = (p: Product) => {
    if (!seen.has(p.id)) {
      seen.add(p.id);
      candidatesByName.push(p);
    }
  };

  if (code) {
    const codeMatches: Product[] = [];
    for (const p of catalog) {
      if (p.getCode() === code || p.id === code) {
        codeMatches.push(p);
      }
    }
    if (codeMatches.length === 1) {
      autoByCode = codeMatches[0];
    }
  }

  if (name && name.length >= 2) {
    for (const p of catalog) {
      const pn = (p.getName() || "").toLowerCase();
      if (!pn) continue;
      if (pn === name || pn.includes(name) || name.includes(pn)) {
        pushName(p);
      }
    }
  }

  return { autoByCode, candidatesByName };
}

function normalizeForScore(s: string): string {
  return s
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();
}

function scoreNameMatch(aiName: string, productName: string): number {
  const a = normalizeForScore(aiName);
  const b = normalizeForScore(productName);
  if (!a || !b) return 0;
  if (a === b) return 100;
  if (b.includes(a) || a.includes(b)) return 80;
  const wordsA = a.split(/\s+/).filter(w => w.length >= 3);
  const wordsB = new Set(b.split(/\s+/).filter(w => w.length >= 3));
  if (wordsA.length === 0) return 0;
  const commonCount = wordsA.filter(w => wordsB.has(w)).length;
  if (commonCount === 0) return 0;
  const overlap = commonCount / Math.max(wordsA.length, wordsB.size);
  return Math.round(overlap * 70);
}

export type ScoredCandidatesResult = {
  autoByCode: Product | null;
  candidates: Product[];
};

/**
 * Versão com scoring para o modal de match.
 * Retorna autoByCode (código exato) e candidatos ordenados por relevância de nome.
 */
export function scoredCandidatesForMatch(
  row: { code: string; name: string },
  catalog: Product[],
  limit = 4
): ScoredCandidatesResult {
  const code = row.code?.trim();
  let autoByCode: Product | null = null;
  const autoByCodeId = new Set<string>();

  if (code) {
    const codeMatches = catalog.filter(p => p.getCode() === code || p.id === code);
    if (codeMatches.length === 1) {
      autoByCode = codeMatches[0];
      autoByCodeId.add(autoByCode.id);
    }
  }

  const scored: Array<{ product: Product; score: number }> = [];
  for (const p of catalog) {
    if (autoByCodeId.has(p.id)) continue;
    const s = scoreNameMatch(row.name ?? "", p.getName() ?? "");
    if (s > 0) scored.push({ product: p, score: s });
  }

  scored.sort((a, b) => b.score - a.score);
  return {
    autoByCode,
    candidates: scored.slice(0, limit).map(x => x.product),
  };
}
